/**
 * Cliente HTTP minimalista contra la API POS: el server local (edición offline)
 * o el cloud bajo `/pos` (edición online). Mismo contrato en ambos.
 *
 * Auth: lee el token del auth store (Zustand). Adjunta `Authorization: Bearer ...`.
 * Errores: 4xx/5xx lanzan `ApiError` con el envelope del backend.
 */

import type { ApiErrorBody } from "@/types/api.ts";

// VITE_API_URL es el nombre nuevo (sirve para ambas ediciones); VITE_LOCAL_API_URL
// se mantiene por compatibilidad con los .env/Dockerfile existentes.
export const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  (import.meta.env.VITE_LOCAL_API_URL as string | undefined) ??
  "http://localhost:8001";

export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || body.error_code);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = body.error_code;
    this.details = body.details;
  }
}

type TokenGetter = () => string | null;
type TerminalIdGetter = () => string | null;
type TenantRucGetter = () => string | null;
type Unauthorized = () => void;

let getToken: TokenGetter = () => null;
let getTerminalId: TerminalIdGetter = () => null;
let getTenantRuc: TenantRucGetter = () => null;
let onUnauthorized: Unauthorized = () => {};

export function configureApi(opts: {
  getToken: TokenGetter;
  getTerminalId?: TerminalIdGetter;
  getTenantRuc?: TenantRucGetter;
  onUnauthorized?: Unauthorized;
}): void {
  getToken = opts.getToken;
  if (opts.getTerminalId) getTerminalId = opts.getTerminalId;
  if (opts.getTenantRuc) getTenantRuc = opts.getTenantRuc;
  if (opts.onUnauthorized) onUnauthorized = opts.onUnauthorized;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  searchParams?: Record<string, string | number | boolean | undefined | null>;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, searchParams, headers, ...rest } = opts;
  const url = buildUrl(path, searchParams);
  const finalHeaders = new Headers(headers);
  if (!finalHeaders.has("Accept")) finalHeaders.set("Accept", "application/json");

  // FormData: que el browser ponga el Content-Type con boundary; nunca stringify.
  const isFormData = body instanceof FormData;
  if (body !== undefined && !isFormData && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }

  const token = getToken();
  if (token) finalHeaders.set("Authorization", `Bearer ${token}`);

  // X-Terminal-Id es opcional a nivel cliente: solo unos pocos endpoints lo
  // requieren (open_session, etc.). Si está disponible siempre lo mandamos —
  // no estorba en endpoints que lo ignoran.
  if (!finalHeaders.has("X-Terminal-Id")) {
    const terminalId = getTerminalId();
    if (terminalId) finalHeaders.set("X-Terminal-Id", terminalId);
  }

  // Edición online: identifica la empresa en endpoints sin JWT (login,
  // selector de cajeros). El server local lo ignora.
  if (!finalHeaders.has("X-Tenant-Ruc")) {
    const ruc = getTenantRuc();
    if (ruc) finalHeaders.set("X-Tenant-Ruc", ruc);
  }

  const response = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body:
      body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const isJson = (response.headers.get("content-type") ?? "").includes("application/json");
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const errBody: ApiErrorBody = isJson
      ? (payload as ApiErrorBody)
      : { error_code: "HttpError", message: String(payload) };
    if (response.status === 401) onUnauthorized();
    throw new ApiError(response.status, errBody);
  }

  return payload as T;
}

function buildUrl(
  path: string,
  searchParams?: Record<string, string | number | boolean | undefined | null>,
): string {
  // Sin la "/" inicial: `new URL("/x", "https://cloud/pos/")` descartaría el
  // prefijo `/pos` de la edición online.
  const relative = path.replace(/^\/+/, "");
  const url = new URL(relative, API_BASE_URL.endsWith("/") ? API_BASE_URL : `${API_BASE_URL}/`);
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  del: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "DELETE" }),
};
